import React, { Component } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Button, Card, Nav, Tab } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { fetchApplicantById, updateApplicant, deleteApplicant, reprocessApplicant } from "api/menu/hr/applicants/actions";
import { fetchJobPostingById } from "api/menu/hr/job-postings/actions";
import { downloadResume } from "api/menu/hr/resume/actions";
import { fetchApplicantActions } from "api/menu/hr/applicant-actions/actions";
import { getApplicantDuplicationInfo } from "api/menu/hr/duplications/actions";
import { openCustomModal, closeCustomModal } from "components/custom/modals";
import ResumeViewerModal from "components/menu/hr/resume/ResumeViewerModal";
import DuplicationInfo from "./duplication-info";
import InterviewSection from "./interview-section";
import { getApplicantStatusLabel, ApplicantStatus } from "data/applicant-statuses";
import { getApplicantActionTypeLabel, getApplicantActionBadgeClass } from "data/applicant-action-types";
import { Popconfirm } from "antd";
import RejectionReasonModal from "./rejection-reason-modal";
import UserTypes from "components/auth/user-types";
import ApplicantScoreDisplay from "./applicant-score-display";

const ActionModal = React.lazy(() => import("./action-modal"));
const ApplicantModal = React.lazy(() => import("./applicant-modal"));

class ApplicantView extends Component {
    constructor(props) {
        super(props);
        this.state = { 
            activeTab: null,
            showResumeModal: false,
            resumeFileId: null,
            resumeFilename: null,
            reprocessing: false
        };
    }

    componentDidMount() {
        const id = parseInt(this.props.match.params.id, 10);
        this.props.fetchApplicantById(id);
        this.props.fetchApplicantActions(id);
    }

    componentDidUpdate(prevProps) {
        const a = this.props.applicant;
        if (a && a.jobPostingId && (!prevProps.applicant || prevProps.applicant.jobPostingId !== a.jobPostingId)) {
            this.props.fetchJobPostingById(a.jobPostingId);
        }

        if (prevProps.match.params.id !== this.props.match.params.id) {
            this.setState({ activeTab: null });
        }
    }

    getFirstVisibleTab = (applicant) => {
        if (applicant?.parsedJson) {
            return "scoring";
        }
        return "duplications";
    };

    handleViewResume = (fileId, filename) => {
        this.setState({
            showResumeModal: true,
            resumeFileId: fileId,
            resumeFilename: filename
        });
    };

    handleDownloadResume = (fileId, filename) => {
        this.props.downloadResume(fileId, filename || "resume.pdf");
    };

    handleCloseResumeModal = () => {
        this.setState({
            showResumeModal: false,
            resumeFileId: null,
            resumeFilename: null
        });
    };

    openActionModal = (actionType) => {
        const applicantId = parseInt(this.props.match.params.id, 10);
        const modalObj = {
            id: `action-${actionType}-${applicantId}`,
            header: `Record ${actionType} Action`,
            modalSettings: { size: "md" },
            children: (
                <React.Suspense fallback={<div className="m-2 text-center">Loading...</div>}>
                    <ActionModal
                        applicantId={applicantId}
                        actionType={actionType}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSaved={() => {
                            this.props.closeCustomModal(modalObj);
                            // Refresh actions list
                            this.props.fetchApplicantActions(applicantId);
                        }}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    isStatusDisabled = (statusValue) => {
        const currentStatus = this.props.applicant?.status;
        if (!currentStatus) return false;
        
        // Handle both string and numeric status values
        const currentStatusValue = typeof currentStatus === 'string' 
            ? (ApplicantStatus[currentStatus] !== undefined ? ApplicantStatus[currentStatus] : parseInt(currentStatus, 10))
            : currentStatus;
            
        return currentStatusValue === statusValue;
    };

    handleStatusUpdate = (newStatus, statusLabel) => {
        const applicantId = parseInt(this.props.match.params.id, 10);
        const applicant = this.props.applicant || {};
        const jobPosting = this.props.jobPosting || {};
        
        console.log('Status update triggered:', { newStatus, statusLabel, applicantId, applicant, jobPosting });
        
        if (window.confirm(`Are you sure you want to change the status to "${statusLabel}"?`)) {
            // Determine assignedToId based on status
            let assignedToId = applicant.assignedToId || null;
            
            // If changing to HrReview, assign to HR from job posting
            if (newStatus === ApplicantStatus.HrReview && jobPosting.hrId) {
                assignedToId = jobPosting.hrId;
                console.log('Assigning to HR:', jobPosting.hrId, 'from job posting');
            }
            
            // Include only the fields that the API expects
            const updatePayload = {
                id: applicantId,
                firstName: applicant.firstName || '',
                lastName: applicant.lastName || '',
                email: applicant.email || null,
                phone: applicant.phone || null,
                assignedToId: assignedToId,
                status: newStatus
            };
            
            console.log('Update payload:', updatePayload);
            
            this.props.updateApplicant(applicantId, updatePayload, (success) => {
                console.log('Update result:', success);
                if (success) {
                    // Refresh applicant data to show updated status
                    this.props.fetchApplicantById(applicantId);
                }
            });
        }
    };

    handleRejectClick = () => {
        console.log('Reject button clicked, opening modal');
        console.log('Current applicant status:', this.props.applicant?.status);
        console.log('Is status disabled:', this.isStatusDisabled(ApplicantStatus.RejectedManually));
        
        const applicantId = parseInt(this.props.match.params.id, 10);
        
        const modalObj = {
            id: `rejection-${applicantId}`,
            header: "Reject Applicant",
            modalSettings: { size: "md" },
            children: (
                <RejectionReasonModal
                    applicantId={applicantId}
                    onCancel={() => this.props.closeCustomModal(modalObj)}
                    onConfirmed={(rejectionReason) => {
                        this.props.closeCustomModal(modalObj);
                        this.handleRejectionConfirmed(rejectionReason);
                    }}
                />
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    handleRejectionConfirmed = (rejectionReason) => {
        // Now update the status to rejected
        const applicantId = parseInt(this.props.match.params.id, 10);
        const applicant = this.props.applicant || {};
        const jobPosting = this.props.jobPosting || {};
        
        // Determine assignedToId based on status
        let assignedToId = applicant.assignedToId || null;
        
        // Include only the fields that the API expects
        const updatePayload = {
            id: applicantId,
            firstName: applicant.firstName || '',
            lastName: applicant.lastName || '',
            email: applicant.email || null,
            phone: applicant.phone || null,
            assignedToId: assignedToId,
            status: ApplicantStatus.RejectedManually
        };
        
        console.log('Rejection update payload:', updatePayload);
        
        this.props.updateApplicant(applicantId, updatePayload, (success) => {
            console.log('Rejection update result:', success);
            if (success) {
                // Refresh applicant data and actions to show updated status and new comment
                this.props.fetchApplicantById(applicantId);
                this.props.fetchApplicantActions(applicantId);
            }
        });
    };

    handleEdit = () => {
        const applicantId = parseInt(this.props.match.params.id, 10);
        const modalObj = {
            id: `applicant-${applicantId}`,
            header: "Update Applicant",
            modalSettings: { size: "lg" },
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <ApplicantModal
                        applicantId={applicantId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSaved={() => {
                            this.props.closeCustomModal(modalObj);
                            // Refresh applicant data
                            this.props.fetchApplicantById(applicantId);
                        }}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    handleReprocess = () => {
        const applicantId = parseInt(this.props.match.params.id, 10);
        this.setState({ reprocessing: true });
        this.props.reprocessApplicant(applicantId, () => {
            this.setState({ reprocessing: false });
            // Refresh applicant data after reprocessing
            setTimeout(() => this.props.fetchApplicantById(applicantId), 500);
        });
    };

    handleDelete = (applicantId, applicantName) => {
        this.props.deleteApplicant(applicantId, applicantName, () => {
            // Navigate back to applicants list after deletion
            this.props.history.push('/hr/applicants');
        });
    };

    renderActionsTab() {
        const { actions, loading } = this.props;
        
        return (
            <Card className="mt-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Actions</h5>
                    <div>
                        <Button 
                            variant="outline-info" 
                            size="sm" 
                            className="mr-2"
                            onClick={() => this.openActionModal("Contact")}
                        >
                            Record Contact
                        </Button>
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => this.openActionModal("Comment")}
                        >
                            Add Comment
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-3">Loading actions...</div>
                    ) : actions && actions.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Action Type</th>
                                        <th>Details</th>
                                        <th>User</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {actions.map(action => (
                                        <tr key={action.id}>
                                            <td>{action.createdOn ? new Date(action.createdOn).toLocaleString() : 'N/A'}</td>
                                            <td>
                                                <span className={`badge ${getApplicantActionBadgeClass(action.action)}`}>
                                                    {getApplicantActionTypeLabel(action.action)}
                                                </span>
                                            </td>
                                            <td style={{ maxWidth: "300px", wordWrap: "break-word" }}>
                                                {action.details}
                                            </td>
                                            <td>{action.userName || `User ${action.userId}`}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center text-muted py-3">
                            <p>No actions recorded for this applicant.</p>
                            <small>Use the buttons above to record contact or comment actions.</small>
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    }

    renderDefinitionList(object) {
        if (!object || typeof object !== "object") return null;
        const entries = Object.entries(object).filter(([k, v]) => v !== null && v !== undefined && v !== "");
        if (!entries.length) return null;
        return (
            <dl>
                {entries.map(([key, value]) => (
                    <div key={key} className="row mb-2">
                        <dt className="col-sm-3 text-capitalize">{key.split(/(?=[A-Z_])/).join(" ")}</dt>
                        <dd className="col-sm-9">
                            {Array.isArray(value)
                                ? this.renderArray(value)
                                : (typeof value === "object" ? this.renderDefinitionList(value) : String(value))}
                        </dd>
                    </div>
                ))}
            </dl>
        );
    }

    renderArray(arr) {
        if (!Array.isArray(arr) || !arr.length) return null;
        const isPrimitive = arr.every(item => item === null || ["string", "number", "boolean"].includes(typeof item));
        if (isPrimitive) {
            return (
                <ul className="mb-0">
                    {arr.map((v, i) => <li key={i}>{String(v)}</li>)}
                </ul>
            );
        }
        return (
            <div>
                {arr.map((obj, i) => (
                    <Card key={i} className="mb-2">
                        <Card.Body>
                            {typeof obj === "object" ? this.renderDefinitionList(obj) : String(obj)}
                        </Card.Body>
                    </Card>
                ))}
            </div>
        );
    }

    renderParsedResume(resume) {
        if (!resume) return null;
        const basics = resume.basics || {};
        const work = resume.work || [];
        const education = resume.education || [];
        const skills = resume.skills || [];
        const languages = resume.languages || [];
        const certifications = resume.certifications || [];
        const projects = resume.projects || [];
        const volunteer = resume.volunteer || [];

        const headerItems = [];
        if (basics.name) headerItems.push(<div key="name"><strong>Name:</strong> {basics.name}</div>);
        if (basics.label) headerItems.push(<div key="label"><strong>Title:</strong> {basics.label}</div>);
        if (basics.email) headerItems.push(<div key="email"><strong>Email:</strong> {basics.email}</div>);
        if (basics.phone) headerItems.push(<div key="phone"><strong>Phone:</strong> {basics.phone}</div>);
        if (basics.url) headerItems.push(<div key="url"><strong>Website:</strong> {basics.url}</div>);
        if (basics.location) {
            const loc = basics.location;
            const locStr = [loc.address, loc.city, loc.region, loc.postalCode, loc.countryCode].filter(Boolean).join(", ");
            if (locStr) headerItems.push(<div key="location"><strong>Location:</strong> {locStr}</div>);
        }

        return (
            <Card className="mt-3">
                <Card.Header>Parsed Resume</Card.Header>
                <Card.Body>
                    {headerItems}
                    {basics.summary && (
                        <div className="mt-3">
                            <strong>Summary</strong>
                            <div style={{ whiteSpace: "pre-wrap" }}>{basics.summary}</div>
                        </div>
                    )}
                    {Array.isArray(work) && work.length > 0 && (
                        <div className="mt-3">
                            <strong>Work</strong>
                            {this.renderArray(work)}
                        </div>
                    )}
                    {Array.isArray(education) && education.length > 0 && (
                        <div className="mt-3">
                            <strong>Education</strong>
                            {this.renderArray(education)}
                        </div>
                    )}
                    {Array.isArray(skills) && skills.length > 0 && (
                        <div className="mt-3">
                            <strong>Skills</strong>
                            {this.renderArray(skills)}
                        </div>
                    )}
                    {Array.isArray(certifications) && certifications.length > 0 && (
                        <div className="mt-3">
                            <strong>Certifications</strong>
                            {this.renderArray(certifications)}
                        </div>
                    )}
                    {Array.isArray(languages) && languages.length > 0 && (
                        <div className="mt-3">
                            <strong>Languages</strong>
                            {this.renderArray(languages)}
                        </div>
                    )}
                    {Array.isArray(projects) && projects.length > 0 && (
                        <div className="mt-3">
                            <strong>Projects</strong>
                            {this.renderArray(projects)}
                        </div>
                    )}
                    {Array.isArray(volunteer) && volunteer.length > 0 && (
                        <div className="mt-3">
                            <strong>Volunteer</strong>
                            {this.renderArray(volunteer)}
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    }

    renderScoring(scoring) {
        if (!scoring) return null;
        const overall = scoring.overallScore ?? scoring.totalScore ?? null;
        const criteria = Array.isArray(scoring.criteria) ? scoring.criteria : (Array.isArray(scoring.items) ? scoring.items : null);
        return (
            <Card className="mt-3">
                <Card.Header>Scoring</Card.Header>
                <Card.Body>
                    {overall !== null && (
                        <div className="mb-3"><strong>Overall Score:</strong> {overall}</div>
                    )}
                    {criteria && criteria.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-sm table-striped mb-0">
                                <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Score</th>
                                    <th>Weight</th>
                                    <th>Notes</th>
                                </tr>
                                </thead>
                                <tbody>
                                {criteria.map((c, i) => (
                                    <tr key={i}>
                                        <td>{c.category || c.name || c.id || i + 1}</td>
                                        <td>{c.score != null ? c.score : c.value}</td>
                                        <td>{c.weight != null ? c.weight : ""}</td>
                                        <td>{c.explanation || c.notes || ""}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : this.renderDefinitionList(scoring)}
                </Card.Body>
            </Card>
        );
    }

    render() {
        const a = this.props.applicant || {};
        const jp = this.props.jobPosting || {};
        const jobPostingLabel = jp && jp.title ? `${jp.title} - ${jp.cityName || ""}, ${jp.provinceName || ""}` : "";
        const jobPostingNeedsAttention = !!jp.id && !jp.jobDescription;
        
        // Check if user is supervisor (restricted permissions)
        const isSupervisor = this.props.auth.user.role.includes(UserTypes.supervisor);
        const activeTab = this.state.activeTab || this.getFirstVisibleTab(a);

        return (
            <div className="dashboard">
                <h3 className="mb-4">Applicant</h3>

                <Card>
                    <Card.Body>
                        <div className="row">
                            <div className="col-md-4">
                                <div>
                                    <strong>Job Posting Applied:</strong> {jobPostingLabel}
                                    {jobPostingNeedsAttention && (
                                        <FontAwesomeIcon
                                            icon="exclamation"
                                            className="text-danger ml-2"
                                            title="Needs attention: no job description set. AI resume scoring and interview features are disabled until it is updated."
                                        />
                                    )}
                                </div>
                                <div><strong>Name:</strong> {a.fullName || `${a.firstName || ""} ${a.lastName || ""}`.trim()}</div>
                                <div><strong>Email:</strong> {a.email || ""}</div>
                                <div><strong>Phone:</strong> {a.phone || ""}</div>
                            </div>
                            <div className="col-md-4">
                                <div><strong>Assigned To:</strong> {a.assignedToName || ""}</div>
                                <div><strong>Status:</strong> {getApplicantStatusLabel(a.status) || ""}</div>
                                <div>
                                    <strong>Final score:</strong>{" "}
                                    <ApplicantScoreDisplay
                                        finalScore={a.finalScore != null ? a.finalScore : a.overallScore}
                                        scoreBreakdown={a.scoreBreakdown}
                                        tooltipId={`applicant-score-detail-${a.id}`}
                                    />
                                </div>
                                <div><strong>Resume:</strong> 
                                    {a.resumeFileId ? (
                                        <div className="mt-1">
                                            <Button 
                                                size="sm" 
                                                variant="outline-primary" 
                                                className="mr-2" 
                                                onClick={() => this.handleViewResume(a.resumeFileId, a.resumeFileName || a.fileName || a.filename || a.originalFileName || `resume-${a.resumeFileId}.pdf`)}
                                            >
                                                <FontAwesomeIcon icon="eye" /> View
                                            </Button>
                                            <Button 
                                                size="sm" 
                                                variant="outline-secondary" 
                                                onClick={() => this.handleDownloadResume(a.resumeFileId, a.resumeFileName || a.fileName || a.filename || a.originalFileName || `resume-${a.resumeFileId}.pdf`)}
                                            >
                                                <FontAwesomeIcon icon="download" /> Download
                                            </Button>
                                        </div>
                                    ) : (
                                        <span className="text-muted"> No resume uploaded</span>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div><strong>Status Change</strong></div>
                                <div className="d-flex flex-wrap">
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm" 
                                        className="mr-2 mb-2"
                                        onClick={this.handleRejectClick}
                                        disabled={this.isStatusDisabled(ApplicantStatus.RejectedManually)}
                                    >
                                        <FontAwesomeIcon icon="thumbs-down" className="mr-1" />
                                        Reject
                                    </Button>
                                    <Button 
                                        variant="outline-warning" 
                                        size="sm" 
                                        className="mr-2 mb-2"
                                        onClick={() => this.handleStatusUpdate(ApplicantStatus.Shortlisted, "Shortlisted")}
                                        disabled={this.isStatusDisabled(ApplicantStatus.Shortlisted)}
                                    >
                                        <FontAwesomeIcon icon="star" className="mr-1" />
                                        ShortList
                                    </Button>
                                    <Button 
                                        variant="outline-info" 
                                        size="sm" 
                                        className="mr-2 mb-2"
                                        onClick={() => this.handleStatusUpdate(ApplicantStatus.SelectedForInterview, "Selected For Interview")}
                                        disabled={this.isStatusDisabled(ApplicantStatus.SelectedForInterview)}
                                    >
                                        <FontAwesomeIcon icon="calendar-check" className="mr-1" />
                                        Select for Interview
                                    </Button>
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        className="mr-2 mb-2"
                                        onClick={() => this.handleStatusUpdate(ApplicantStatus.HrReview, "HR Review")}
                                        disabled={this.isStatusDisabled(ApplicantStatus.HrReview)}
                                    >
                                        <FontAwesomeIcon icon="user-tie" className="mr-1" />
                                        Send To HR
                                    </Button>
                                    <Button 
                                        variant="outline-success" 
                                        size="sm" 
                                        className="mr-2 mb-2"
                                        onClick={() => this.handleStatusUpdate(ApplicantStatus.ToBeHired, "To Be Hired")}
                                        disabled={this.isStatusDisabled(ApplicantStatus.ToBeHired)}
                                    >
                                        <FontAwesomeIcon icon="handshake" className="mr-1" />
                                        Hire
                                    </Button>
                                </div>
                                
                                {!isSupervisor && (
                                    <div className="mt-3">
                                        <div><strong>Actions</strong></div>
                                        <div className="d-flex flex-wrap">
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm" 
                                                className="mr-2 mb-2"
                                                onClick={() => this.handleEdit()}
                                            >
                                                <FontAwesomeIcon icon="pen" className="mr-1" />
                                                Edit
                                            </Button>
                                            <Button 
                                                variant="outline-info" 
                                                size="sm" 
                                                className="mr-2 mb-2"
                                                onClick={() => this.handleReprocess()}
                                                disabled={this.state.reprocessing}
                                            >
                                                {this.state.reprocessing ? (
                                                    <FontAwesomeIcon icon="spinner" spin className="mr-1" />
                                                ) : (
                                                    <FontAwesomeIcon icon="redo" className="mr-1" />
                                                )}
                                                Reprocess
                                            </Button>
                                            <Popconfirm
                                                title={`Delete "${a.fullName || a.email || a.id}"?`}
                                                onConfirm={() => this.handleDelete(parseInt(this.props.match.params.id, 10), a.fullName)}
                                                placement="bottomRight"
                                                okText="Yes"
                                                cancelText="Cancel"
                                            >
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm" 
                                                    className="mr-2 mb-2"
                                                >
                                                    <FontAwesomeIcon icon="times" className="mr-1" />
                                                    Delete
                                                </Button>
                                            </Popconfirm>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Tab.Container activeKey={activeTab} onSelect={(k) => this.setState({ activeTab: k })}>
                    <Nav variant="tabs" className="mt-3">
                        {a.parsedJson && (
                            <>
                                <Nav.Item>
                                    <Nav.Link eventKey="scoring">Scoring Info</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="resume">Parsed Resume</Nav.Link>
                                </Nav.Item>
                            </>
                        )}
                        <Nav.Item>
                            <Nav.Link eventKey="duplications">Duplications</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="actions">Actions</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="interviews">Interviews</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    
                    <Tab.Content>
                        <Tab.Pane eventKey="scoring">
                            {a.parsedJson && (() => {
                                try {
                                    const obj = JSON.parse(a.parsedJson);
                                    return this.renderScoring(obj.scoring || obj.score || obj);
                                } catch (e) { return <div className="mt-3 text-danger">Error parsing scoring data</div>; }
                            })()}
                        </Tab.Pane>
                        <Tab.Pane eventKey="resume">
                            {a.parsedJson && (() => {
                                try {
                                    const obj = JSON.parse(a.parsedJson);
                                    return this.renderParsedResume(obj.parsedResume || obj.resume || obj);
                                } catch (e) { return <div className="mt-3 text-danger">Error parsing resume data</div>; }
                            })()}
                        </Tab.Pane>
                        <Tab.Pane eventKey="duplications">
                            <div className="mt-3">
                                <DuplicationInfo
                                    applicantId={parseInt(this.props.match.params.id, 10)}
                                    duplicationInfo={this.props.duplicationInfo}
                                    loading={this.props.duplicationLoading}
                                    error={this.props.duplicationError}
                                    getApplicantDuplicationInfo={this.props.getApplicantDuplicationInfo}
                                />
                            </div>
                        </Tab.Pane>
                        <Tab.Pane eventKey="actions">
                            {this.renderActionsTab()}
                        </Tab.Pane>
                        <Tab.Pane eventKey="interviews">
                            <InterviewSection
                                applicantId={parseInt(this.props.match.params.id, 10)}
                                applicant={a}
                                jobPostingNeedsAttention={jobPostingNeedsAttention}
                                onApplicantRefresh={() => {
                                    const id = this.props.match.params.id;
                                    this.props.fetchApplicantById(id);
                                    this.props.fetchApplicantActions(id);
                                }}
                            />
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>

                <ResumeViewerModal
                    show={this.state.showResumeModal}
                    onHide={this.handleCloseResumeModal}
                    fileId={this.state.resumeFileId}
                    filename={this.state.resumeFilename}
                />

            </div>
        );
    }
}

ApplicantView.propTypes = {
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    applicant: PropTypes.object,
    jobPosting: PropTypes.object,
    actions: PropTypes.array,
    loading: PropTypes.bool,
    duplicationInfo: PropTypes.object,
    duplicationLoading: PropTypes.bool,
    duplicationError: PropTypes.string,
    auth: PropTypes.object.isRequired,
    fetchApplicantById: PropTypes.func.isRequired,
    fetchJobPostingById: PropTypes.func.isRequired,
    fetchApplicantActions: PropTypes.func.isRequired,
    getApplicantDuplicationInfo: PropTypes.func.isRequired,
    downloadResume: PropTypes.func.isRequired,
    updateApplicant: PropTypes.func.isRequired,
    deleteApplicant: PropTypes.func.isRequired,
    reprocessApplicant: PropTypes.func.isRequired,
    openCustomModal: PropTypes.func.isRequired,
    closeCustomModal: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    applicant: state.applicants.singleApplicant,
    jobPosting: state.jobPostings ? state.jobPostings.singleJobPosting : {},
    actions: state.applicantActions.actions,
    loading: state.applicantActions.loading,
    duplicationInfo: state.duplications ? state.duplications.duplicationInfo : null,
    duplicationLoading: state.duplications ? state.duplications.loading : false,
    duplicationError: state.duplications ? state.duplications.error : null,
    auth: state.auth
});

const mapDispatchToProps = dispatch => ({
    fetchApplicantById: bindActionCreators(fetchApplicantById, dispatch),
    fetchJobPostingById: bindActionCreators(fetchJobPostingById, dispatch),
    fetchApplicantActions: bindActionCreators(fetchApplicantActions, dispatch),
    getApplicantDuplicationInfo: bindActionCreators(getApplicantDuplicationInfo, dispatch),
    downloadResume: bindActionCreators(downloadResume, dispatch),
    updateApplicant: bindActionCreators(updateApplicant, dispatch),
    deleteApplicant: bindActionCreators(deleteApplicant, dispatch),
    reprocessApplicant: bindActionCreators(reprocessApplicant, dispatch),
    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ApplicantView);


